import { apiErrorField } from '@agos/api-client'
import {
  getGetLoyaltyLevelsQueryKey,
  getGetSettingsLoyaltyQueryKey,
  useDeleteLoyaltyLevelsLevelId,
  useGetCustomers,
  useGetLoyaltyLevels,
  useGetSettingsLoyalty,
  usePatchLoyaltyLevelsLevelId,
  usePostCustomersBackfillLevels,
  usePostLoyaltyLevels,
  usePutSettingsLoyalty,
} from '@agos/api-client/customers'
import { formatCents, formatCount, T, tt, useAuth, useModalFocus } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  PageHeader,
  Spinner,
  StatCard,
  TabGroup,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Crown, Heart, Megaphone, Pencil, Plus, Settings2, Trash2, Users, X } from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'

import { CampaignsTab } from './CampaignsTab'
import { mayWrite, readOnlyReason, type LoyaltyTab } from './write-access'

import {
  EMOJI_PALETTE,
  REFERRAL_DISCOUNT_TYPES,
  activeTiers,
  canSaveProgram,
  canSaveReferral,
  canSaveTier,
  deleteWarning,
  distribution,
  divergences,
  emptyTierForm,
  formatMultiplier,
  formatRate,
  holdersOf,
  movedBy,
  programFormOf,
  programPayload,
  programProblems,
  redemptionWorth,
  referralFormOf,
  referralPayload,
  referralProblemMessage,
  referralProblems,
  settingsTiersAreLive,
  starterOf,
  tierFormOf,
  tierPayload,
  tierProblems,
  withEditedTier,
  type ProgramForm,
  type ProgramProblem,
  type ProgramSettings,
  type ReferralForm,
  type Tier,
  type TierForm,
  type TierProblem,
} from './loyalty-tiers'

/**
 * `/loyalty-program` — the programme's identity and its tiers.
 *
 * **FB-097.** Legacy puts two editors for the same five tiers on this page. `LoyaltyLevelsEditor`
 * writes `loyalty_levels`; the *Body za vernosť* block writes eight columns on `loyalty_settings`.
 * `CustomerLoyalty.tierFor` reads `loyalty_settings` **only when `loyalty_levels` has no active
 * row** — and it has five. So the second editor changes nothing, reports success, and would silently
 * diverge from the first the moment either is touched.
 *
 * **FB-098.** 1 108 members, 1 106 on Bronze, one on Silver, one on Gold Master. Nobody on Gold or
 * Elite Master, and the busiest Bronze customer is six orders short of Silver. The page says so,
 * because a 1,7× multiplier on a tier two people have reached is worth knowing about before it is
 * tuned again.
 *
 * The second tab is the campaigns screen — see `CampaignsTab` for FB-166 to FB-169. It writes through
 * a different controller with a different rule, which is why `write-access.ts` answers per tab rather
 * than per page.
 *
 * Ported from legacy `pages/loyalty-program.tsx`, whose settings tab is lines 448–839.
 */

interface CustomerListing {
  customers?: { orderCount?: number | null; isLoyalty?: boolean }[]
}

export function LoyaltyProgramPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user, hasPermission } = useAuth()

  const [tab, setTab] = useState<LoyaltyTab>('settings')
  // §9.758 - the settings tab asks `loyalty_program_edit` (BACKEND.md §10A.413); the campaigns tab keeps its role.
  const writable = mayWrite(tab, user?.systemRole, hasPermission)
  const readOnly = readOnlyReason(tab, user?.systemRole, hasPermission)

  const { data: settingsData, isLoading: loadingSettings } = useGetSettingsLoyalty()
  const { data: levelData, isLoading: loadingLevels } = useGetLoyaltyLevels()
  // The members' order counts, for the distribution. One request the server has no summary for
  // (FB-060's rule: derived, and labelled as derived).
  const { data: memberData } = useGetCustomers({ loyalty: 'true', limit: 10_000, offset: 0 })

  const settings = (settingsData ?? {}) as ProgramSettings
  const tiers = useMemo<Tier[]>(() => (levelData ?? []), [levelData])
  const orderCounts = useMemo(
    () =>
      (((memberData ?? {}) as CustomerListing).customers ?? [])
        .filter((customer) => customer.isLoyalty !== false)
        .map((customer) => customer.orderCount ?? 0),
    [memberData],
  )

  const [program, setProgram] = useState<ProgramForm | null>(null)
  const [referral, setReferral] = useState<ReferralForm | null>(null)
  const [editing, setEditing] = useState<{ id: number | null; form: TierForm } | null>(null)
  const [deletingTier, setDeletingTier] = useState<Tier | null>(null)
  const [backfilling, setBackfilling] = useState(false)

  // Seeded once the settings arrive; `key` on the form remounts it if they change underneath.
  const form = program ?? programFormOf(settings)
  const problems = programProblems(form)
  const spread = distribution(orderCounts, tiers)
  const ladder = activeTiers(tiers)
  const settingsLive = settingsTiersAreLive(tiers)
  const diverged = divergences(settings, tiers)
  const worth = redemptionWorth(form)
  const referralForm = referral ?? referralFormOf(settings)
  const referralIssues = referralProblems(referralForm)

  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Zmena sa neuložila.'),
      variant: 'destructive',
    })
  const refreshSettings = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSettingsLoyaltyQueryKey() })
  }
  const refreshLevels = () => {
    void queryClient.invalidateQueries({ queryKey: getGetLoyaltyLevelsQueryKey() })
  }

  const saveProgram = usePutSettingsLoyalty({
    mutation: {
      onSuccess: () => {
        toast({ title: tt('Nastavenia uložené') })
        refreshSettings()
      },
      onError,
    },
  })
  /* §9.565 - the same endpoint as `saveProgram`, and a separate mutation so the referral card's own
   * button owns its pending state. The bodies are disjoint by design: see `referralPayload`. */
  const saveReferral = usePutSettingsLoyalty({
    mutation: {
      onSuccess: () => {
        toast({ title: tt('Nastavenia uložené') })
        refreshSettings()
      },
      onError,
    },
  })
  const createTier = usePostLoyaltyLevels({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refreshLevels()
      },
      onError,
    },
  })
  const patchTier = usePatchLoyaltyLevelsLevelId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refreshLevels()
      },
      onError,
    },
  })
  const deleteTier = useDeleteLoyaltyLevelsLevelId({
    mutation: {
      onSuccess: () => {
        setDeletingTier(null)
        refreshLevels()
      },
      onError,
    },
  })
  const backfill = usePostCustomersBackfillLevels({
    mutation: {
      onSuccess: (result: unknown) => {
        const updated = (result as { updated?: number }).updated ?? 0
        setBackfilling(false)
        toast({ title: `${tt('Prepočítané')} - ${updated} ${tt('zákazníkov')}` })
        void queryClient.invalidateQueries()
      },
      onError: (error: unknown) => {
        setBackfilling(false)
        onError(error)
      },
    },
  })

  if (loadingSettings || loadingLevels) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Vernostný program')}
        description={tt('Identita programu, body a úrovne, ktoré zákazník stúpa objednávkami.')}
        icon={<Heart className="w-5 h-5" />}
      />

      {/* `variant="bar"` - the owner asked for this bar to match *Kupóny a zľavy* and the rest. It
        * was the only thing separating them: `TabGroup` defaults to `pills`, and the eleven screens
        * with a top-level tab bar — coupons, analytics, reports, employees, stock, the till — all
        * pass `bar`. This one did not. */}
      <TabGroup
        variant="bar"
        tabs={[
          { id: 'settings', label: tt('Nastavenia'), icon: Settings2 },
          { id: 'campaigns', label: tt('Kampane'), icon: Megaphone },
        ]}
        active={tab}
        onChange={(id) => setTab(id as LoyaltyTab)}
      />

      {/* Per tab, not per page: the two halves of this screen write through different controllers. */}
      {readOnly !== null && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-read-only={tab}>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>{readOnly}</span>
        </p>
      )}

      {tab === 'campaigns' && <CampaignsTab writable={writable} />}

      {tab === 'settings' && (
        <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-loyalty-totals>
        <StatCard
          label={tt('Členov')}
          value={formatCount(spread.members)}
          icon={Users}
          color={settings.isActive === true ? 'emerald' : 'amber'}
          sub={settings.isActive === true ? tt('program je zapnutý') : tt('program je vypnutý')}
        />
        <StatCard label={tt('Úrovní')} value={String(ladder.length)} icon={Crown} color="amber" />
        <StatCard
          label={tt('Nikto nedosiahol')}
          value={String(spread.emptyTiers.length)}
          icon={AlertCircle}
          color={spread.emptyTiers.length > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Zľava za body')}
          value={formatRate(worth.effectiveRateBasisPoints)}
          sub={`${worth.minRedeemPoints} ${tt('bodov')} = ${formatCents(worth.valueCents)}`}
          icon={Heart}
          color="violet"
        />
      </div>

      {/* FB-098 - derived from the members this page already loaded, and labelled as derived. */}
      {spread.emptyTiers.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-empty-tiers>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Tieto úrovne nedosiahol ani jeden zákazník:</T>{' '}
            <strong>
              {spread.tiers
                .filter((tier) => tier.members === 0)
                .map((tier) => `${tier.name} (${tier.minOrders}+)`)
                .join(', ')}
            </strong>
            .{' '}
            {spread.shortfall !== null && spread.shortfall > 0 && (
              <>
                <T>Najaktívnejšiemu členovi chýba</T> {spread.shortfall} <T>objednávok.</T>{' '}
              </>
            )}
            <T>Počítané z členov programu, nie zo servera.</T>
          </span>
        </p>
      )}

      {/* FB-097 - said above both editors, because only one of them does anything. */}
      {!settingsLive && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-settings-inert>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>
              Úroveň zákazníka rozhodujú tabuľky úrovní nižšie. Staré polia „prah“ a „násobok“ v
              nastaveniach bodov server použije len vtedy, keď neexistuje ani jedna aktívna úroveň -
              preto ich táto obrazovka nezobrazuje.
            </T>
          </span>
        </p>
      )}

      {diverged.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-settings-diverged>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Staré nastavenia bodov nesúhlasia s úrovňami:</T>{' '}
            {diverged
              .map(
                (item) =>
                  `${item.key} ${item.settingsThreshold}/${item.tierThreshold ?? '-'}`,
              )
              .join(', ')}
            . <T>Platia úrovne.</T>
          </span>
        </p>
      )}

      {/* ─── the programme's identity ─── */}
      <Card title={tt('Program')}>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-3 py-2.5">
          <span className="min-w-0">
            <span className="block text-sm font-medium">
              <T>Program je zapnutý</T>
            </span>
            <span className="block text-[11px] text-muted-foreground">
              <T>Vypnutý program neprideľuje body ani neposúva úrovne.</T>
            </span>
          </span>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setProgram({ ...form, isActive: event.target.checked })}
            aria-label={tt('Program je zapnutý')}
            className="w-5 h-5 rounded accent-primary shrink-0"
          />
        </label>

        {/* §9.454, BACKEND.md §10A.217 - the owner's own label, and their own words for what it is
          * for: *"in vernostný program we need to add new setting 'Kombinovať vernostné body zo
          * zľavami', and make sure this will work also in customer website"*.
          *
          * Six sources of discount stack on a web order and the sum is capped at the bill, so a
          * 50 % coupon plus points worth the other half is an order for nothing. The sentence
          * underneath says what unticking it does to the **points** as well as to the price,
          * because *refused* and *spent anyway* would be the worse outcome and an operator cannot
          * read that off a checkbox. */}
        <label
          className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-3 py-2.5"
          data-combine-points
        >
          <span className="min-w-0">
            <span className="block text-sm font-medium">
              <T>Kombinovať vernostné body so zľavami</T>
            </span>
            <span className="block text-[11px] text-muted-foreground">
              <T>
                Vypnuté: na objednávke, ktorá už má zľavu alebo kupón, sa body neuplatnia - a
                zákazníkovi sa neodpíšu.
              </T>
            </span>
          </span>
          <input
            type="checkbox"
            checked={form.combinePointsWithDiscounts}
            onChange={(event) =>
              setProgram({ ...form, combinePointsWithDiscounts: event.target.checked })
            }
            aria-label={tt('Kombinovať vernostné body so zľavami')}
            className="w-5 h-5 rounded accent-primary shrink-0"
          />
        </label>

        <Field label={tt('Názov programu')}>
          <input
            value={form.programName}
            onChange={(event) => setProgram({ ...form, programName: event.target.value })}
            aria-label={tt('Názov programu')}
            className={INPUT}
          />
        </Field>
        <Field label={tt('Popis')}>
          <textarea
            value={form.description}
            onChange={(event) => setProgram({ ...form, description: event.target.value })}
            rows={2}
            aria-label={tt('Popis')}
            className={cn(INPUT, 'resize-none')}
          />
        </Field>
        <Field label={tt('Vitajúca správa')}>
          <textarea
            value={form.welcomeMessage}
            onChange={(event) => setProgram({ ...form, welcomeMessage: event.target.value })}
            rows={2}
            aria-label={tt('Vitajúca správa')}
            className={cn(INPUT, 'resize-none')}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={tt('Body za euro')}>
            <input
              value={form.pointsPerEuro}
              onChange={(event) => setProgram({ ...form, pointsPerEuro: event.target.value })}
              aria-label={tt('Body za euro')}
              className={INPUT}
            />
          </Field>
          <Field label={tt('Hodnota bodu (€)')}>
            <input
              value={form.pointValue}
              onChange={(event) => setProgram({ ...form, pointValue: event.target.value })}
              aria-label={tt('Hodnota bodu')}
              className={INPUT}
            />
          </Field>
          <Field label={tt('Minimum na uplatnenie')}>
            <input
              value={form.minRedeemPoints}
              onChange={(event) => setProgram({ ...form, minRedeemPoints: event.target.value })}
              aria-label={tt('Minimum na uplatnenie')}
              className={INPUT}
            />
          </Field>
        </div>

        {/* Legacy shows the three numbers and never multiplies them. */}
        <p className="text-xs text-muted-foreground" data-redemption-worth>
          <strong>{worth.minRedeemPoints}</strong> <T>bodov</T> ={' '}
          <strong>{formatCents(worth.valueCents)}</strong> <T>zľavy, na ktorú treba utratiť</T>{' '}
          <strong>{formatCents(Math.round(worth.eurosToEarn * 100))}</strong> -{' '}
          <T>teda program vracia</T> <strong>{formatRate(worth.effectiveRateBasisPoints)}</strong>{' '}
          <T>z obratu.</T>
        </p>

        {problems.length > 0 && (
          <div className="space-y-1" data-program-problems>
            {problems.map((problem) => (
              <p key={problem.kind} className="flex items-start gap-1.5 text-xs text-red-500">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <ProgramProblemText problem={problem} />
              </p>
            ))}
          </div>
        )}

        <button
          type="button"
          data-save-program
          onClick={() => saveProgram.mutate({ data: programPayload(form) })}
          disabled={!writable || saveProgram.isPending || !canSaveProgram(problems)}
          className={cn('px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {saveProgram.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť program</T>}
        </button>
      </Card>

      {/* ─── §9.565: what a referral is worth, which the server has been applying all along ─── */}
      <Card title={tt('Odporúčanie zákazníka')}>
        <p className="text-xs text-muted-foreground">
          <T>
            Keď nový zákazník zadá kód od existujúceho, dostane zľavu na objednávku a odporúčajúci
            dostane body. Kým bolo toto vypnuté v nastaveniach, zľava sa aj tak uplatňovala.
          </T>
        </p>

        <label className="flex items-start justify-between gap-3 p-3 rounded-xl border border-border cursor-pointer">
          <span className="text-sm">
            <T>Program odporúčaní</T>
            <span className="block text-[11px] text-muted-foreground">
              {referralForm.enabled ? <T>Zapnutý</T> : <T>Vypnutý</T>}
            </span>
          </span>
          <input
            type="checkbox"
            data-referral-enabled
            checked={referralForm.enabled}
            onChange={(event) => setReferral({ ...referralForm, enabled: event.target.checked })}
            aria-label={tt('Program odporúčaní')}
            className="w-5 h-5 rounded accent-primary shrink-0"
          />
        </label>

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label={tt('Typ zľavy')}>
            <select
              data-referral-type
              value={referralForm.discountType}
              onChange={(event) =>
                setReferral({
                  ...referralForm,
                  /* The element only ever holds one of the two, so this is the narrowing rather than
                   * a claim about the string: `REFERRAL_DISCOUNT_TYPES` builds the options. */
                  discountType: event.target.value === 'euro' ? 'euro' : 'percent',
                })
              }
              aria-label={tt('Typ zľavy')}
              className={INPUT}
            >
              {REFERRAL_DISCOUNT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type === 'percent' ? '%' : '€'}
                </option>
              ))}
            </select>
          </Field>
          <Field label={referralForm.discountType === 'percent' ? tt('Zľava (%)') : tt('Zľava (€)')}>
            <input
              data-referral-value
              value={referralForm.discountValue}
              onChange={(event) => setReferral({ ...referralForm, discountValue: event.target.value })}
              aria-label={tt('Zľava za odporúčanie')}
              className={INPUT}
            />
          </Field>
          <Field label={tt('Body pre odporúčajúceho')}>
            <input
              data-referral-points
              value={referralForm.rewardPoints}
              onChange={(event) => setReferral({ ...referralForm, rewardPoints: event.target.value })}
              aria-label={tt('Body pre odporúčajúceho')}
              className={INPUT}
            />
          </Field>
        </div>

        {referralIssues.length > 0 && (
          <div className="space-y-1" data-referral-problems>
            {referralIssues.map((problem) => (
              <p key={problem.kind} className="flex items-start gap-1.5 text-xs text-red-500">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                {referralProblemMessage(problem)}
              </p>
            ))}
          </div>
        )}

        <button
          type="button"
          data-save-referral
          onClick={() => saveReferral.mutate({ data: referralPayload(referralForm) })}
          disabled={!writable || saveReferral.isPending || !canSaveReferral(referralIssues)}
          className={cn('px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {saveReferral.isPending ? (
            <Spinner className="w-4 h-4 mx-auto" />
          ) : (
            <T>Uložiť odporúčania</T>
          )}
        </button>
      </Card>

      {/* ─── the ladder that decides ─── */}
      <Card
        title={tt('Úrovne')}
        action={
          <button
            type="button"
            data-add-tier
            disabled={!writable}
            onClick={() => setEditing({ id: null, form: emptyTierForm() })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            <T>Pridať úroveň</T>
          </button>
        }
      >
        <div className="space-y-1.5" data-tiers>
          {spread.tiers.map((population) => {
            const tier = ladder.find((candidate) => candidate.key === population.key)
            if (tier === undefined) return null
            return (
              <div
                key={tier.id}
                className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-3 py-2.5"
                data-tier-row={tier.key}
              >
                <span className="text-xl shrink-0">{tier.emoji ?? '🏆'}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{tier.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {tier.minOrders ?? 0}+ <T>objednávok</T> · {formatMultiplier(tier.multiplier)}{' '}
                    <T>body</T>
                  </p>
                </div>
                <span
                  className={cn(
                    'text-xs font-bold tabular-nums shrink-0',
                    population.members === 0 ? 'text-amber-500' : 'text-muted-foreground',
                  )}
                  data-tier-members
                >
                  {formatCount(population.members)}
                </span>
                <button
                  type="button"
                  disabled={!writable}
                  onClick={() => setEditing({ id: tier.id ?? null, form: tierFormOf(tier) })}
                  aria-label={`${tt('Upraviť úroveň')} ${tier.name ?? ''}`}
                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted shrink-0 disabled:opacity-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                {/* §9.35 - `starterOf` rather than the flag alone. The starter cannot be deleted
                  * because everybody begins there, and `isStarter !== true` trusts the data to have
                  * one flagged: with none flagged this screen would offer to delete the tier every
                  * customer actually starts at. `starterOf` falls back to the weakest active tier,
                  * which is what the ladder does. Bronze is flagged today, so this changes nothing
                  * now and holds when it is not. */}
                {starterOf(tiers)?.key !== tier.key && (
                  <button
                    type="button"
                    disabled={!writable}
                    onClick={() => setDeletingTier(tier)}
                    aria-label={`${tt('Zmazať úroveň')} ${tier.name ?? ''}`}
                    className="p-1.5 rounded-lg border border-border text-red-500 hover:bg-red-500/10 shrink-0 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )
          })}
        </div>

        <p className="text-[11px] text-muted-foreground">
          <T>
            Úroveň sa nikdy neznižuje - kto dosiahol Gold, zostáva Gold aj keď sa prahy zvýšia.
            Zmena prahov preto posunie iba tých, ktorí novú úroveň dosiahnu.
          </T>
        </p>

        <button
          type="button"
          data-backfill
          disabled={!writable}
          onClick={() => setBackfilling(true)}
          className="px-4 py-2.5 rounded-xl border border-border text-sm font-semibold hover:bg-muted/50 disabled:opacity-50"
        >
          <T>Prepočítať úrovne všetkým</T>
        </button>
      </Card>

      {editing !== null && (
        <TierDialog
          form={editing.form}
          editingId={editing.id}
          tiers={tiers}
          orderCounts={orderCounts}
          saving={createTier.isPending || patchTier.isPending}
          onChange={(next) => setEditing({ ...editing, form: next })}
          onClose={() => setEditing(null)}
          onSave={() => {
            const payload = tierPayload(editing.form)
            if (editing.id === null) createTier.mutate({ data: payload })
            else patchTier.mutate({ levelId: editing.id, data: payload })
          }}
        />
      )}

      <ConfirmDialog
        open={deletingTier !== null}
        onClose={() => setDeletingTier(null)}
        onConfirm={() =>
          deletingTier?.id !== undefined && deleteTier.mutate({ levelId: deletingTier.id })
        }
        title={`${tt('Zmazať úroveň')} ${deletingTier?.name ?? ''}?`}
        description={
          deletingTier === null
            ? ''
            : deleteWarning(deletingTier, holdersOf(deletingTier.key ?? '', orderCounts, tiers))
        }
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={deleteTier.isPending}
      />

      <ConfirmDialog
        open={backfilling}
        onClose={() => setBackfilling(false)}
        onConfirm={() => backfill.mutate()}
        title={tt('Prepočítať úrovne všetkým zákazníkom?')}
        // The server's own rule, said before the button rather than discovered after it.
        description={tt(
          'Každému zákazníkovi sa nastaví úroveň podľa počtu objednávok a súčasných prahov. Toto je jediná operácia, ktorá úroveň aj zníži.',
        )}
        confirmLabel={tt('Prepočítať')}
        loading={backfill.isPending}
      />
        </>
      )}
    </div>
  )
}

// ─── the tier editor ─────────────────────────────────────────────────────────

function TierDialog({
  form,
  editingId,
  tiers,
  orderCounts,
  saving,
  onChange,
  onClose,
  onSave,
}: {
  form: TierForm
  editingId: number | null
  tiers: Tier[]
  orderCounts: number[]
  saving: boolean
  onChange: (next: TierForm) => void
  onClose: () => void
  onSave: () => void
}) {
  const others = tiers.filter((tier) => tier.id !== editingId)
  const problems = tierProblems(form, others)
  const after = withEditedTier(tiers, form, editingId)
  const moved = movedBy(orderCounts, tiers, after)

  /* §9.495, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card (§9.469); the close matches the scrim's own tap,
     which is unconditional here because this overlay holds no write of its own (§9.491). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editingId === null ? tt('Nová úroveň') : tt('Upraviť úroveň')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-tier-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm">
            {editingId === null ? tt('Nová úroveň') : `${tt('Upraviť')} ${form.name || form.key}`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={tt('Kľúč')}>
              <input
                value={form.key}
                onChange={(event) => onChange({ ...form, key: event.target.value })}
                aria-label={tt('Kľúč')}
                disabled={editingId !== null && form.isStarter}
                className={cn(INPUT, 'font-mono')}
              />
            </Field>
            <Field label={tt('Názov')}>
              <input
                value={form.name}
                onChange={(event) => onChange({ ...form, name: event.target.value })}
                aria-label={tt('Názov')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Prah (objednávok)')}>
              <input
                value={form.minOrders}
                onChange={(event) => onChange({ ...form, minOrders: event.target.value })}
                aria-label={tt('Prah')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Násobok bodov')}>
              <input
                value={form.multiplier}
                onChange={(event) => onChange({ ...form, multiplier: event.target.value })}
                aria-label={tt('Násobok bodov')}
                className={INPUT}
              />
            </Field>
          </div>

          <Field label={tt('Ikona')}>
            <div className="flex flex-wrap gap-1" data-emoji-palette>
              {EMOJI_PALETTE.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => onChange({ ...form, emoji })}
                  aria-label={emoji}
                  className={cn(
                    'w-8 h-8 rounded-lg border text-lg leading-none',
                    form.emoji === emoji ? 'border-primary bg-primary/10' : 'border-border',
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </Field>

          <Field label={tt('Farba')}>
            <input
              type="color"
              value={form.color}
              onChange={(event) => onChange({ ...form, color: event.target.value })}
              aria-label={tt('Farba')}
              className="w-16 h-9 rounded-lg border border-border bg-background"
            />
          </Field>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => onChange({ ...form, active: event.target.checked })}
              aria-label={tt('Aktívna')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Aktívna - neaktívnu úroveň server pri určovaní preskočí</T>
          </label>

          {/* What this edit would do, before it is saved. */}
          {problems.length === 0 && (
            <p className="text-xs text-muted-foreground" data-tier-impact>
              {moved === 0 ? (
                <T>Táto zmena neposunie ani jedného zákazníka.</T>
              ) : (
                <>
                  <T>Táto zmena posunie</T> <strong>{moved}</strong> <T>zákazníkov.</T>
                </>
              )}{' '}
              <T>Úroveň sa nikdy neznižuje, takže posun platí len nahor.</T>
            </p>
          )}

          {problems.length > 0 && (
            <div className="space-y-1" data-tier-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    problem.kind === 'multiplier-absurd' ? 'text-amber-500' : 'text-red-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <TierProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !canSaveTier(problems)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť úroveň</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function TierProblemText({ problem }: { problem: TierProblem }) {
  if (problem.kind === 'no-key') return <T>Kľúč je povinný.</T>
  if (problem.kind === 'bad-key') {
    return <T>Kľúč môže mať len malé písmená, číslice a podčiarkovník.</T>
  }
  if (problem.kind === 'duplicate-key') {
    return <T>Tento kľúč už iná úroveň má - zlúčili by sa jej členovia.</T>
  }
  if (problem.kind === 'no-name') return <T>Názov je povinný.</T>
  if (problem.kind === 'threshold-not-whole') return <T>Prah je celé číslo objednávok.</T>
  if (problem.kind === 'threshold-negative') return <T>Prah nemôže byť záporný.</T>
  if (problem.kind === 'duplicate-threshold') {
    return (
      <span>
        <T>Na tomto prahu už je</T> <strong>{problem.other}</strong> -{' '}
        <T>server by ticho vybral jednu z nich.</T>
      </span>
    )
  }
  if (problem.kind === 'multiplier-below-one') {
    return <T>Násobok pod 1 znamená, že vyššia úroveň dáva menej bodov.</T>
  }
  if (problem.kind === 'starter-not-zero') {
    return <T>Základná úroveň musí začínať na nule objednávok.</T>
  }
  return (
    <span>
      <T>Násobok</T> {formatMultiplier(problem.value)} <T>je nezvyčajne vysoký - je to zámer?</T>
    </span>
  )
}

function ProgramProblemText({ problem }: { problem: ProgramProblem }) {
  if (problem.kind === 'no-name') return <T>Názov programu je povinný.</T>
  if (problem.kind === 'points-per-euro-zero') return <T>Body za euro musia byť viac ako nula.</T>
  if (problem.kind === 'point-value-zero') return <T>Hodnota bodu musí byť viac ako nula.</T>
  if (problem.kind === 'redeem-not-whole') return <T>Minimum na uplatnenie je celé číslo.</T>
  return <T>Minimum na uplatnenie nemôže byť záporné.</T>
}

// ─── small pieces ────────────────────────────────────────────────────────────

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Card({
  title,
  action,
  children,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
